import { useAppDisplayModeContext } from '@archipro-design/aria';

import { RoccoLink } from '@rocco/components/link';
import { useProfessionalOverviewShared } from '@modules/professional/hook/use-professional-overview-shared';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import { useEnquiryVisibility } from '~/modules/enquiry/hooks/use-enquiry-visibility';
import {
    DetailSaveButton,
    ReadMoreText,
    detailCta,
    stripHtml,
} from '~/ui-rocco/pages/detail-shared';
import { SectionLabel } from '~/ui-rocco/pages/home-page/components/SectionLabel';
import { formatProjectValueRange } from '../utils/project-value';

/** Anchor id the "Get in touch" CTA scrolls to (the inline enquiry form). */
export const CONTACT_ENQUIRY_ANCHOR = 'professional-contact-enquiry';

/**
 * Contact tab intro (Figma 2193:11058) — the "Let's connect" heading + company
 * summary on the dark section, then the "(01 — Location)" divider above the map
 * + enquiry form. On desktop the typical-project-value stat + Get in touch
 * (scrolls to the form) / Visit Website / Save CTAs sit on the right; on mobile
 * they live in the persistent social-profile header + the inline form below.
 */
export const ProfessionalContactConnectSection = () => {
    const { professionalOverview } = useProfessionalOverviewShared();
    const professional = useProfessionalInitialRouteData();
    const { desktop } = useAppDisplayModeContext();
    const { showEnquiryCTA } = useEnquiryVisibility(professional);

    const summary = professionalOverview.BlockSummary
        ? stripHtml(professionalOverview.BlockSummary)
        : undefined;

    // Typical project-value range (from PriceRanges) — same mapping as the
    // overview Summary section so the figure matches across tabs.
    const valueRange = formatProjectValueRange(
        professionalOverview.PriceRanges,
    );

    const scrollToForm = () => {
        document
            .getElementById(CONTACT_ENQUIRY_ANCHOR)
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    return (
        <section className="w-full overflow-hidden bg-[#181818] text-white max-md:px-6 max-md:pt-10 md:px-[3.125rem] md:pt-[3rem]">
            <div className="mx-auto w-full max-w-[113.75rem]">
                {/* "Let's connect" + (desktop) value / CTAs. */}
                <div className="flex md:items-start md:justify-between">
                    <h2 className="m-0 font-sans font-normal text-white max-md:text-[2rem] max-md:leading-[1.08] max-md:tracking-[-0.02em] md:text-[12.5rem] md:leading-[11.25rem] md:tracking-[-0.525rem]">
                        Let&apos;s connect
                    </h2>

                    {desktop && (
                        // No top margin — the cluster tops out level with the
                        // heading (48px under the sub-nav), matching its spot
                        // on every other tab.
                        <div className="flex flex-col gap-[1.875rem] md:items-end">
                            {/* Reserve the stat's height even when there's no value
                            so the CTAs below keep their spot. */}
                            <div
                                className={`flex items-baseline justify-end gap-[0.75rem]${
                                    valueRange ? '' : ' invisible'
                                }`}
                            >
                                <p className="m-0 font-sans leading-[1.2] font-medium whitespace-nowrap text-white/60 md:text-[0.9375rem] md:tracking-[-0.047rem]">
                                    Typical project value
                                </p>
                                <p className="m-0 font-sans leading-[0.9] font-medium text-white md:text-[3.125rem] md:tracking-[-0.156rem]">
                                    {valueRange || ' '}
                                </p>
                            </div>

                            <div className="flex items-center gap-2">
                                {showEnquiryCTA ? (
                                    <button
                                        type="button"
                                        onClick={scrollToForm}
                                        className={detailCta('filled-dark')}
                                    >
                                        Get in touch
                                        <svg
                                            viewBox="0 0 24 24"
                                            aria-hidden="true"
                                            className="size-[0.83em]"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                        >
                                            <path d="M5 12h13M12 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                ) : null}
                                {professionalOverview.Website ? (
                                    <RoccoLink
                                        to={professionalOverview.Website}
                                        newTab
                                        className={detailCta('outlined-dark')}
                                    >
                                        Visit Website
                                    </RoccoLink>
                                ) : null}
                                <DetailSaveButton
                                    pinId={professional.ID}
                                    pinType="ProfessionalPin"
                                    theme="dark"
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Company summary. */}
                {summary ? (
                    <div className="max-md:mt-6 md:mt-[5.9375rem] md:max-w-[60.375rem]">
                        <ReadMoreText
                            text={summary}
                            lines={3}
                            className="font-sans leading-[1.25] font-medium text-white max-md:text-base md:text-[2.0625rem] md:tracking-[-0.0515rem]"
                            toggleClassName="text-white"
                        />
                    </div>
                ) : null}

                {/* "(01 — Location)" divider above the map + form. */}
                <div className="max-md:pt-12 md:pt-[10rem]">
                    <div className="h-px w-full bg-white/20" />
                    <SectionLabel className="mt-[1.062rem] block text-white max-md:mt-3 max-md:text-[0.875rem] md:text-[2rem]">
                        (01 — Location)
                    </SectionLabel>
                </div>
            </div>
        </section>
    );
};
